<script setup>
import {computed, defineEmits, defineProps, nextTick, onMounted, ref} from 'vue'
import {useDisplay} from "vuetify";

const props = defineProps({
    product: {
        type: Object,
        required: true
    },
    activeColor: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['color-click', 'color-mouseover', 'color-mouseleave'])

const display = useDisplay()
const showAllColors = ref(false)
const totalRows = ref(1)
const btnDisplayClass = computed(() => totalRows.value === 1 ? 'text-center' : 'justify-center')
const colorsContainerHeight = ref(33)

const hasActiveColor = computed(() => {
    return !!props.activeColor[props.product.reference]
})

onMounted(() => {
    nextTick(() => {
        totalRows.value = calculateTotalRows()
    })
})

const hasColor = (color) => {
    return color.code !== 'SEC';
}

const getGradientStyle = (color) => {
    return `background: linear-gradient(127deg, ${color.hex ?? '#FFFFFF'} 50%, ${color.hexSec ?? color.hex ?? '#FFFFFF'} 50%);`
}

const findVariationByColor = (product, color) => {
    if (!product.variations) return null

    const colorTitle = color.title?.toLowerCase()

    return product.variations.find(variation =>
        variation.color?.toLowerCase() === colorTitle
    )
}

const handleColorClick = (productRef, color, product) => {
    emit('color-click', {productRef, color, product})
}

const handleColorMouseEnter = (productRef, color, product) => {
    emit('color-mouseover', {productRef, color, product})
}

const resetHoveredDetails = (productRef) => {
    emit('color-mouseleave', {productRef})
}

const displayBullets = computed(() => {
    if (display.width.value <= 768) return 9
    if (display.width.value <= 1279) return 13
    if (display.width.value <= 1440) return 8
    return 10
})


const calculateTotalRows = () => {
    return Math.ceil(props.product.colors.length / displayBullets.value)
}

const calculateColorsHeight = () => {
    const totalRows = calculateTotalRows()
    return Math.max(33, totalRows * 33)
}

const toggleShowAllColors = (show) => {
    if (!hasActiveColor.value) {
        if (show) {
            colorsContainerHeight.value = calculateColorsHeight()
        } else {
            colorsContainerHeight.value = 33
        }
        showAllColors.value = show
    }
}

//Utilizar caso tenha mais de uma referencia que necessite mostrar o nome.
const nomColorRef = (color) => {
    return "Cor Única"
}

</script>

<template>
    <v-sheet v-if="props.product.variations !== undefined" class="mx-3 my-1">
        <v-responsive :height="colorsContainerHeight"
                      @mouseover="toggleShowAllColors(true)"
                      @mouseleave="toggleShowAllColors(false)"
                      style="transition: height 200ms ease; overflow: hidden;"
                      :class="btnDisplayClass"
        >
            <template v-for="(color, index) in props.product.colors" :key="index">
                <v-btn
                    v-if="hasColor(color)"
                    :style="getGradientStyle(color)"
                    style="margin-right: 2%;"
                    :class="{ 'v-btn--active': activeColor[props.product.reference] && findVariationByColor(props.product, color)?.media_url === activeColor[props.product.reference] }"
                    class="mb-1 pa-0 ma-1"
                    min-width="25px"
                    width="25px"
                    height="25px"
                    :title="color.title"
                    variant="flat"
                    rounded
                    elevation="0"
                    border
                    @click.stop="handleColorClick(props.product.reference, color, props.product)"
                    @mouseover="handleColorMouseEnter(props.product.reference, color, props.product)"
                    @mouseleave="resetHoveredDetails(props.product.reference)"
                />
                <v-chip v-else border color="grey-darken-1">
                    <p class="text-subtitle-2">{{ nomColorRef(color) }}</p>
                </v-chip>
            </template>
        </v-responsive>
        <v-divider inset class="mx-auto mt-3"></v-divider>
        <v-sheet class="d-flex justify-center" style="margin-top: -13px;">
            <v-chip
                size="small"
                variant="flat"
                color="white"
                style="cursor: pointer;"
                :text="props.product.colors.length + (props.product.colors.length > 1 ? ' cores' : ' cor')"
            />
        </v-sheet>
    </v-sheet>
</template>

<style scoped>
.v-btn--active {
    opacity: 0.8;
    transform: scale(1.2);
    transition: all 0.2s ease;
    box-shadow: 0 0 3px 1px rgb(0 0 0 / 30%) !important
}
</style>